// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/users/UserLifecycleModal.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Button, Field, TextArea } from "../../../design-system/components";
import ModalShell from "../../compliance/modals/shared/ModalShell";
import ModalSection from "../../compliance/modals/shared/ModalSection";

const COPY = {
  suspend: {
    eyebrow: "Suspend access",
    title: "Suspend this member?",
    description: "This member will immediately lose access to the organization. Their membership and audit history remain available, and an administrator can restore access later.",
    submit: "Suspend member",
    danger: true,
  },
  reactivate: {
    eyebrow: "Restore access",
    title: "Reactivate this member?",
    description: "This member will regain access according to their current roles and permissions. The change is organization-scoped and audited.",
    submit: "Reactivate member",
    danger: false,
  },
  remove: {
    eyebrow: "Remove member",
    title: "Remove this member?",
    description: "The membership will be removed, active role assignments will be withdrawn, and the full action will remain in administrative audit history.",
    submit: "Remove member",
    danger: true,
  },
};

export default function UserLifecycleModal({
  open,
  mode,
  user,
  saving,
  error,
  onClose,
  onConfirm,
}) {
  const [reason, setReason] = useState("");
  const config = useMemo(() => COPY[mode] || COPY.suspend, [mode]);

  useEffect(() => {
    if (open) setReason("");
  }, [open, mode, user?.user_id]);

  if (!user) return null;

  const submit = async (event) => {
    event.preventDefault();
    await onConfirm?.(reason.trim());
  };

  return (
    <ModalShell
      className="enterprise-admin-modal-shell"
      overlayClassName="enterprise-admin-modal-overlay"
      headerClassName="enterprise-admin-modal-header"
      bodyClassName="enterprise-admin-modal-body"
      open={open}
      eyebrow={config.eyebrow}
      title={config.title}
      subtitle={user.display_name || user.email || user.user_id}
      onClose={saving ? undefined : onClose}
      maxWidth="700px"
      accentBorder={config.danger ? "rgba(248, 113, 113, 0.58)" : "rgba(106, 168, 255, 0.45)"}
    >
      <form className="enterprise-admin-user-modal-form" onSubmit={submit}>
        <ModalSection title="What will change" description={config.description} badge="Audited" badgeTone={config.danger ? "warning" : "info"}>
          <Field label="Administrative reason" required help="Use a clear reason that will make sense during a future audit review.">
            {({ id, "aria-describedby": describedBy }) => (
              <TextArea
                id={id}
                aria-describedby={describedBy}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Explain why this access change is required."
                rows={4}
                required
                autoFocus
              />
            )}
          </Field>

          {error ? (
            <div className="enterprise-admin-inline-error" role="alert">
              <strong>Action blocked</strong>
              <span>{error}</span>
            </div>
          ) : null}
        </ModalSection>

        <div className="enterprise-admin-user-modal-actions">
          <Button type="submit" variant={config.danger ? "danger" : "primary"} disabled={saving || !reason.trim()}>
            {saving ? "Applying change…" : config.submit}
          </Button>
          <Button type="button" variant="ghost" disabled={saving} onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </ModalShell>
  );
}
